/**
 * ParticleSystem - lightweight visual FX (impact sparks + dash trails).
 *
 * Owns its own particle arrays. Gameplay code spawns effects, then main.js calls the
 * per-frame update methods each tick and the draw methods during the render pass (sparks
 * and dash trails draw at different z-layers, so they stay as separate draw calls).
 *
 * Pure of game state: callers pass in world positions. `sparks` is public so bespoke
 * one-off bursts (e.g. the helico ember puff) can push custom particles directly.
 *
 * Extracted verbatim from main.js — behaviour is unchanged.
 */

export class ParticleSystem {
  constructor() {
    this.sparks = [];
    this.dashTrails = [];
  }

  /** Radial burst of impact sparks (default gold/amber). */
  spawnSparks(x, y, n = 6, colors = null) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = 80 + Math.random() * 160;
      this.sparks.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s - 60,
        life: 0,
        dur: 0.35 + Math.random() * 0.25,
        size: 1 + Math.random() * 2,
        color: colors
          ? (colors[Math.floor(Math.random() * colors.length)] || colors[0])
          : (Math.random() < 0.5 ? '#ffd37a' : '#ffb84a'),
      });
    }
  }

  /**
   * Cyan dash trail puff. spreadW/spreadH default to the player's (constant) size so
   * existing call sites need no argument changes.
   */
  spawnDashTrail(x, y, spreadW = 24, spreadH = 36) {
    for (let i = 0; i < 3; i++) {
      this.dashTrails.push({
        x: x + (Math.random() - 0.5) * spreadW,
        y: y + (Math.random() - 0.5) * spreadH,
        life: 0,
        dur: 0.2 + Math.random() * 0.15,
        size: 2 + Math.random() * 3,
        alpha: 0.6 + Math.random() * 0.4,
      });
    }
  }

  updateSparks(dt) {
    const GRAV = 800;
    for (const p of this.sparks) {
      p.life += dt;
      p.vy += GRAV * dt * 0.6;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    this.sparks = this.sparks.filter(p => p.life < p.dur);
  }

  drawSparks(ctx) {
    if (!this.sparks.length) return;
    ctx.save();
    for (const p of this.sparks) {
      const t = Math.min(1, p.life / p.dur);
      ctx.globalAlpha = 1 - t;
      ctx.fillStyle = p.color;
      ctx.fillRect(Math.round(p.x), Math.round(p.y), p.size, p.size);
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  updateDashTrails(dt) {
    for (const p of this.dashTrails) {
      p.life += dt;
    }
    this.dashTrails = this.dashTrails.filter(p => p.life < p.dur);
  }

  drawDashTrails(ctx) {
    if (!this.dashTrails.length) return;
    ctx.save();
    for (const p of this.dashTrails) {
      const t = Math.min(1, p.life / p.dur);
      ctx.globalAlpha = (1 - t) * p.alpha;
      ctx.fillStyle = '#7dd3fc'; // Light cyan color for dash trail
      ctx.beginPath();
      ctx.arc(Math.round(p.x), Math.round(p.y), p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }
}
